import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Megaphone, Phone, ShieldAlert, ClipboardCheck, Plus, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import ItineraryTimeline from "@/components/ItineraryTimeline";
import { Loading } from "@/components/auth/RequireAuth";
import { useApi, errMsg, pkr, dateRange } from "@/lib/api";

function todayDay(start, days) {
  const diff = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(`${start}T00:00:00`)) / 86400000) + 1;
  return diff >= 1 && diff <= days ? diff : null;
}

function Notices({ d, api, reload }) {
  const respond = async (cid, accept) => {
    try { await api.post(`/bookings/${d.booking.id}/consent/${cid}`, { accept }); toast.success(accept ? "Thanks, change accepted" : "Change declined: full experience refund issued"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const active = d.booking.status === "confirmed";
  const open = active ? d.change_requests.filter((c) => c.status === "awaiting_consent" && !c.my_response) : [];
  const emergencies = d.communications.filter((c) => c.kind === "emergency");
  const rest = d.communications.filter((c) => c.kind !== "emergency" && c.kind !== "consent");
  return (
    <div className="space-y-4">
      {open.map((c) => (
        <div key={c.id} className="rounded-xl border-2 border-secondary bg-secondary/5 p-5" data-testid={`consent-request-${c.id}`}>
          <div className="eyebrow text-secondary">Material itinerary change · your consent is needed</div>
          <p className="mt-2">{c.summary}</p>
          <div className="mt-4 rounded-lg bg-white p-4"><ItineraryTimeline blocks={c.proposed_blocks} startDate={d.listing.start_date} defaultOpen="first" /></div>
          {d.booking.is_owner ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button className="rounded-full" onClick={() => respond(c.id, true)} data-testid="consent-accept-btn">Accept the change</Button>
              <Button variant="outline" className="rounded-full" onClick={() => respond(c.id, false)} data-testid="consent-decline-btn">Decline and get a full refund</Button>
            </div>
          ) : <p className="mt-3 text-sm text-muted-foreground">The booking owner will respond.</p>}
        </div>
      ))}
      {emergencies.map((c) => (
        <div key={c.id} className="flex gap-3 rounded-xl border border-destructive/40 bg-destructive/5 p-5" data-testid="emergency-notice">
          <ShieldAlert className="h-5 w-5 shrink-0 text-destructive" />
          <div><div className="font-semibold">{c.title}</div><p className="mt-1 text-sm">{c.body}</p><div className="mt-2 text-xs text-muted-foreground">{c.by} · {new Date(c.at).toLocaleString()}</div></div>
        </div>
      ))}
      {rest.map((c) => (
        <div key={c.id} className="flex gap-3 rounded-xl border border-border bg-white p-5" data-testid="logistics-notice">
          <Megaphone className="h-5 w-5 shrink-0 text-secondary" strokeWidth={1.5} />
          <div><div className="flex items-center gap-2 font-semibold">{c.title}<Badge variant="outline" className="capitalize">{c.kind}</Badge></div><p className="mt-1 text-sm text-muted-foreground">{c.body}</p><div className="mt-2 text-xs text-muted-foreground">{c.by} · {new Date(c.at).toLocaleString()}</div></div>
        </div>
      ))}
      {!open.length && !d.communications.length && <p className="text-sm text-muted-foreground" data-testid="notices-empty">No notices yet. Logistics updates from your organizer appear here.</p>}
    </div>
  );
}

function Guests({ d, api, reload }) {
  const [rows, setRows] = useState(d.guests.map(({ name, email, phone, emergency_contact }) => ({ name, email, phone, emergency_contact })));
  const save = async () => {
    try { await api.put(`/bookings/${d.booking.id}/guests`, rows.filter((r) => r.name.trim())); toast.success("Guest list saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const upd = (i, k, v) => setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const editable = d.booking.is_owner && d.booking.status === "confirmed";
  return (
    <div className="space-y-3" data-testid="guests-panel">
      <p className="text-sm text-muted-foreground">{d.booking.kind === "private" ? `Private buy-out: assign up to ${d.booking.seats} guests. Guests who sign in with the email you add here will see this portal too.` : "Participants on this booking."}</p>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded-xl border border-border bg-white p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]" data-testid={`guest-row-${i}`}>
          {["name", "email", "phone", "emergency_contact"].map((k) => (
            <Input key={k} disabled={!editable} value={r[k] || ""} placeholder={k.replace("_", " ")} onChange={(e) => upd(i, k, e.target.value)} data-testid={`guest-${k}-${i}`} />
          ))}
          {editable && <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`guest-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>}
        </div>
      ))}
      {editable && (
        <div className="flex gap-3">
          {rows.length < d.booking.seats && <Button variant="outline" className="rounded-full" onClick={() => setRows([...rows, { name: "", email: "", phone: "", emergency_contact: "" }])} data-testid="guest-add-btn"><Plus className="mr-1 h-4 w-4" />Add guest</Button>}
          <Button className="rounded-full" onClick={save} data-testid="guests-save-btn">Save guest list</Button>
        </div>
      )}
    </div>
  );
}

function Support({ d, api, reload }) {
  const [reason, setReason] = useState("");
  const refundNow = Math.round(d.booking.subtotal * d.refund_pct_now / 100);
  const dispute = async () => {
    try { await api.post(`/bookings/${d.booking.id}/dispute`, { reason }); setReason(""); toast.success("Sent to GOSHA ops for manual review"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async () => {
    try { const { data } = await api.post(`/bookings/${d.booking.id}/cancel`); toast.success(`Cancelled. Refund: ${pkr(data.refund_pkr)}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-border bg-white p-5" data-testid="support-contact">
        <Phone className="h-5 w-5 text-secondary" strokeWidth={1.5} />
        <div className="mt-3 font-semibold">On-trip support</div>
        <div className="mt-1 text-2xl font-display text-primary" data-testid="support-phone">{d.listing.support_phone}</div>
        <p className="mt-2 text-sm text-muted-foreground">Organizer: {d.listing.host_stack.organizer?.name}. Operator: {d.listing.host_stack.operator?.name}. Meeting point: {d.listing.meeting_point}.</p>
      </div>
      <div className="rounded-xl border border-border bg-white p-5">
        <AlertTriangle className="h-5 w-5 text-secondary" strokeWidth={1.5} />
        <div className="mt-3 font-semibold">Raise an issue</div>
        <Textarea className="mt-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tell us what went wrong. A person at GOSHA reviews every case." data-testid="dispute-reason-input" />
        <Button className="mt-3 rounded-full" disabled={!reason.trim()} onClick={dispute} data-testid="dispute-submit-btn">Send for review</Button>
        {d.disputes.map((x) => <div key={x.id} className="mt-3 text-sm text-muted-foreground" data-testid="dispute-item">“{x.reason}” · <Badge variant="outline">{x.status}</Badge>{x.resolution && ` · ${x.resolution}${x.refund_pkr ? ` ${pkr(x.refund_pkr)}` : ""}`}</div>)}
      </div>
      {d.booking.is_owner && d.booking.status === "confirmed" && (
        <div className="rounded-xl border border-border bg-white p-5 lg:col-span-2">
          <div className="font-semibold">Cancel this booking</div>
          <p className="mt-1 text-sm text-muted-foreground">Under the policy you'd get <b data-testid="refund-preview">{d.refund_pct_now}% ({pkr(refundNow)})</b> of the experience price back today. The service fee is non-refundable.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="outline" className="mt-3 rounded-full border-destructive/40 text-destructive hover:bg-destructive hover:text-white" data-testid="cancel-booking-btn">Cancel booking</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Cancel your booking?</AlertDialogTitle><AlertDialogDescription>You'll receive {pkr(refundNow)}. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Keep booking</AlertDialogCancel><AlertDialogAction onClick={cancel} data-testid="cancel-booking-confirm">Yes, cancel</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}

function Checkin({ d, api, reload }) {
  const [note, setNote] = useState(d.checkin?.note || "");
  const send = async () => {
    try { await api.post(`/bookings/${d.booking.id}/checkin`, { delivered: true, note }); toast.success("Thanks for confirming"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-xl rounded-xl border border-border bg-white p-5" data-testid="checkin-panel">
      <ClipboardCheck className="h-5 w-5 text-secondary" strokeWidth={1.5} />
      <div className="mt-3 font-semibold">Confirm the experience was delivered</div>
      <p className="mt-1 text-sm text-muted-foreground">Your confirmation is part of how GOSHA verifies completion before the host is paid.</p>
      {d.checkin ? <p className="mt-3 text-sm text-primary" data-testid="checkin-done">Confirmed on {new Date(d.checkin.at).toLocaleDateString()}.</p> : (
        <>
          <Textarea className="mt-3" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" data-testid="checkin-note-input" />
          <Button className="mt-3 rounded-full" onClick={send} disabled={d.booking.status !== "confirmed"} data-testid="checkin-submit-btn">Yes, it was delivered</Button>
        </>
      )}
    </div>
  );
}

export default function GuestPortal() {
  const { id } = useParams();
  const api = useApi();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/bookings/${id}/portal`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api, id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;
  const l = d.listing;
  const active = d.booking.status === "confirmed";
  const openConsents = active ? d.change_requests.filter((c) => c.status === "awaiting_consent" && !c.my_response).length : 0;
  const hasEmergency = active && d.communications.some((c) => c.kind === "emergency");
  return (
    <div data-testid="guest-portal-page">
      <section className="grain bg-primary text-white">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-12 md:grid-cols-[1fr_auto] md:px-8">
          <div>
            <div className="eyebrow text-white/60">Guest portal · {d.booking.code}</div>
            <h1 className="mt-2 text-3xl tracking-tight sm:text-4xl" data-testid="portal-title">{l.title}</h1>
            <p className="mt-2 text-white/75">{dateRange(l.start_date, l.end_date)} · {l.destination} · {d.booking.kind === "private" ? "Private buy-out" : `${d.booking.seats} seat(s)`}</p>
          </div>
          <div className="flex flex-wrap items-start gap-2">
            <Badge className="bg-white text-primary hover:bg-white" data-testid="portal-booking-status">{d.booking.status}</Badge>
            <Badge variant="outline" className="border-white/40 text-white" data-testid="portal-escrow-status">Payment {d.booking.escrow_status.replace("_", " ")}</Badge>
            {d.booking.refunded_pkr > 0 && <Badge variant="outline" className="border-white/40 text-white">Refunded {pkr(d.booking.refunded_pkr)}</Badge>}
          </div>
        </div>
      </section>
      {hasEmergency && <div className="bg-destructive px-5 py-3 text-center text-sm text-white" data-testid="emergency-banner">Emergency notice from your organizer. Open Notices.</div>}
      <div className="mx-auto max-w-6xl px-5 py-10 md:px-8">
        <Tabs defaultValue={openConsents || hasEmergency ? "notices" : "itinerary"}>
          <TabsList className="flex h-auto flex-wrap justify-start bg-transparent p-0">
            {[["itinerary", "Itinerary"], ["notices", `Notices${openConsents ? ` (${openConsents})` : ""}`], ["guests", "Guests"], ["support", "Support"], ["checkin", "Completion"]].map(([v, label]) => (
              <TabsTrigger key={v} value={v} className="rounded-full px-4 data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`portal-tab-${v}`}>{label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="itinerary" className="mt-8"><ItineraryTimeline blocks={d.itinerary} startDate={l.start_date} highlightDay={todayDay(l.start_date, l.days)} /></TabsContent>
          <TabsContent value="notices" className="mt-8"><Notices d={d} api={api} reload={load} /></TabsContent>
          <TabsContent value="guests" className="mt-8"><Guests key={d.guests.length} d={d} api={api} reload={load} /></TabsContent>
          <TabsContent value="support" className="mt-8"><Support d={d} api={api} reload={load} /></TabsContent>
          <TabsContent value="checkin" className="mt-8"><Checkin d={d} api={api} reload={load} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
